/**
 * Common API types and interfaces
 */

// Base response interfaces
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  totalRows: number;
  pageNo?: number;
  pageSize?: number;
}

// Common shared types
export interface ImageInfo {
  imageUrl: string;
  primary: boolean;
  type: string;
}

export interface AmenityInfo {
  id: number;
  name: string;
  priceType: string;
  amenityIconName?: string;
  weworkIdentifier?: string;
}

export interface TaxInfo {
  taxId: number;
  taxCode: string;
  taxRate: number;
}

export interface TaxDetail {
  taxCode: string;
  taxRate: number;
  taxAmount: number;
}

// Location and address types
export interface LocationInfo {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  zip?: string;
  country: string;
  timezoneIanaId?: string;
  latitude: number;
  longitude: number;
}

// Common enums
export enum BookingType {
  HOURLY = 'Hourly',
  DAILY = 'Daily',
  MONTHLY = 'Monthly'
}

export enum BookingStatus {
  PENDING = 'Pending',
  CONFIRMED = 'Confirmed',
  CANCELLED = 'Cancelled',
  COMPLETED = 'Completed'
}

// Common filter types
export interface PaginationParams {
  pageNo?: number;
  pageSize?: number;
}

export interface SearchParams {
  keyword?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface LocationParams {
  latitude?: number;
  longitude?: number;
  radius?: number;
}

/**
 * Pagination constants used across client applications
 */
export const PaginationConstants = {
  /** Standard page size for most API endpoints */
  DEFAULT_PAGE_SIZE: 10,
  
  /** Domain-specific page sizes */
  DOMAIN_SPECIFIC: {
    /** Page size for booking-related endpoints */
    BOOKING_PAGE_SIZE: 100,
    /** Page size for customer listing endpoints */
    CUSTOMER_PAGE_SIZE: 100,
  },
  
  /** Maximum allowed page size */
  MAX_PAGE_SIZE: 500,
} as const;

// Export types for better TypeScript support
export type PaginationConstantKeys = keyof typeof PaginationConstants; 